import { CloseCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Modal, QRCode, Space } from "antd";
import { useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";

/**
 * 等级证书:1:1 复刻旧站 Canvas 渲染
 * (fastj/client/src/views/inx/inxGuide/components/certificate/index.vue)
 * 495×736 @2x,背景 certificate.jpg + logo.png + stamp.png + 左下二维码。
 */

interface Props {
  open: boolean;
  onClose: () => void;
  data: {
    reg_no: string;
    title: string;
    register: string | null;
    level: string | null;
    created_at: string;
  };
}

const W = 495;
const H = 736;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

export function LevelCertificate({ open, onClose, data }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const qrBoxRef = useRef<HTMLDivElement | null>(null);

  // 二维码指向公开详情页(新站规范地址 /inx/guide/{id})
  const params = useParams({ strict: false }) as { guideId?: string };
  const detailUrl = params.guideId ? `${window.location.origin}/inx/guide/${params.guideId}` : null;

  useEffect(() => {
    if (!open || !detailUrl) return;

    let cancelled = false;
    (async () => {
      // 等一帧让隐藏的 antd QRCode(canvas 形态)先渲染出来
      await new Promise((r) => setTimeout(r, 50));
      if (cancelled) return;

      const canvas = canvasRef.current;
      const qrCanvas = qrBoxRef.current?.querySelector("canvas");
      if (!canvas || !qrCanvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      try {
        const [bg, logo, stampImg] = await Promise.all([
          loadImage("/portal/certificate.jpg"),
          loadImage("/portal/logo.png"),
          loadImage("/portal/stamp.png"),
        ]);
        if (cancelled) return;

        // 2 倍画布提高清晰度(旧站同款)
        canvas.width = W * 2;
        canvas.height = H * 2;
        canvas.style.width = `${W}px`;
        canvas.style.height = `${H}px`;
        ctx.scale(2, 2);

        // 背景
        ctx.drawImage(bg, 0, 0, W, H);

        // logo(居中,y=150)
        ctx.drawImage(logo, (W - 138) / 2, 150, 138, 34);

        // 平台名(中英两行,先设字体再量宽,居中)
        ctx.fillStyle = "#000";
        ctx.textBaseline = "alphabetic";
        ctx.font = "bold 14px Arial";
        const title = "全球服务标准注册及循证评价平台,";
        ctx.fillText(title, (W - ctx.measureText(title).width) / 2, 150 + 34 + 50);
        ctx.font = "bold 12px Arial";
        const subTitle = "TRUST ( Transparent Registry for Unified Standards and Testing )";
        const subTitleY = 150 + 34 + 50 + 20;
        ctx.fillText(subTitle, (W - ctx.measureText(subTitle).width) / 2, subTitleY);

        // 评价结果(有等级才绘制;A/B/C 级)
        if (data.level && data.level !== "0") {
          ctx.font = "bold 18px Arial";
          ctx.fillText(`${data.level}级`, 240, subTitleY + 60);
        }

        // 名称 / 注册号
        ctx.font = "bold 10px Arial";
        const nameY = subTitleY + 100;
        ctx.fillText(`名称:${data.title}`, 70, nameY);
        const regNoY = nameY + 20;
        ctx.fillText(`注册号:${data.reg_no ?? ""}`, 70, regNoY);

        // 注册单位(33 字折行,续行缩进 50)
        const registerLines = data.register?.match(/.{1,33}/g) ?? [];
        let lastY = regNoY;
        registerLines.forEach((line, i) => {
          const y = regNoY + 20 + i * 14;
          ctx.fillText(i === 0 ? `注册单位:${line}` : line, i === 0 ? 70 : 120, y);
          lastY = y;
        });

        // 注册时间(旧站:submitTime ?? createTime,YYYY.M)
        ctx.fillText(`注册时间:${dayjs(data.created_at).format("YYYY.M")}`, 70, lastY + 20);

        // 印章右下、二维码左下(各 100×100)
        ctx.drawImage(stampImg, W - 150, H - 150, 100, 100);
        ctx.drawImage(qrCanvas, 60, H - 150, 100, 100);
      } catch (err) {
        console.error("证书图片加载失败", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, detailUrl, data]);

  function download() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `certificate-${data.reg_no || "trust"}.png`;
    link.click();
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      closeIcon={<CloseCircleOutlined />}
      centered
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ display: "flex", justifyContent: "center", padding: "16px 0 0" }}>
        <canvas ref={canvasRef} style={{ border: "1px solid #000", maxWidth: "100%" }} />
      </div>
      <div
        style={{
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 16px",
        }}
      >
        <Space>
          <Button type="primary" icon={<DownloadOutlined />} onClick={download}>
            下载证书图片
          </Button>
          <Button onClick={onClose}>关闭</Button>
        </Space>
      </div>
      {/* 离屏 antd QRCode(canvas 形态),供 drawImage 取样 */}
      {detailUrl && (
        <div
          ref={qrBoxRef}
          style={{ position: "absolute", left: -9999, top: -9999, pointerEvents: "none" }}
          aria-hidden
        >
          <QRCode type="canvas" value={detailUrl} size={200} bordered={false} />
        </div>
      )}
    </Modal>
  );
}
